import { useCallback } from "react";
import { Button } from "@rg/ui/components/Button";
import { Notification as UiNotification } from "@rg/ui/components/Notification";
import type { NotificationSize } from "@rg/ui/components/Notification";
import { clsx } from "clsx";
import { useNotificationStore } from "@/common/store/notificationStore";
import type { NotificationButton, NotificationViewModel } from "@/common/types/notification";
import { NOTIFICATION_EXIT_ANIMATION_DURATION } from "./constants";
import "./Notification.scss";

type NotificationProps = NotificationViewModel & {
  size: NotificationSize;
};

const toneByVariant = {
  question: "question",
  success: "success",
  error: "error",
  warning: "warning",
  info: "information",
} as const;

export const Notification = ({
  title,
  id,
  description,
  variant,
  actionButton,
  secondaryButton,
  isClosing,
  size,
}: NotificationProps) => {
  const close = useNotificationStore((s) => s.close);
  const hasActions = Boolean(actionButton || secondaryButton);
  const isComplex = Boolean(description || hasActions);

  const handleClose = useCallback(() => {
    close(id, NOTIFICATION_EXIT_ANIMATION_DURATION);
  }, [close, id]);

  const onButtonClick = useCallback(
    (button?: NotificationButton) => {
      close(id, NOTIFICATION_EXIT_ANIMATION_DURATION);
      button?.onClick();
    },
    [close, id]
  );

  const actions = hasActions ? (
    <>
      {secondaryButton ? (
        <Button
          variant="link-inverse"
          className="Notification-SecondaryButton"
          size={size === "mobile" ? "lg" : "xl"}
          onClick={() => onButtonClick(secondaryButton)}
          aria-label={secondaryButton.title}
        >
          {secondaryButton.title}
        </Button>
      ) : null}
      {actionButton ? (
        <Button
          variant="secondary"
          size={size === "mobile" ? "lg" : "xl"}
          onClick={() => onButtonClick(actionButton)}
          aria-label={actionButton.title}
        >
          {actionButton.title}
        </Button>
      ) : null}
    </>
  ) : undefined;

  return (
    <UiNotification
      className={clsx(
        "Notification",
        {
          "Notification-leave": isClosing,
          "Notification-enter": !isClosing,
        }
      )}
      tone={toneByVariant[variant]}
      size={size}
      variant={isComplex ? "title" : "default"}
      title={isComplex ? title : undefined}
      body={isComplex ? description : title}
      showClose
      closeAriaLabel="Close notification"
      onClose={handleClose}
      actions={actions}
    />
  );
};
